import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BookingPassengerFormRow } from '../lib/bookingPassengers';
import { formatDobConfirmDisplay, formatGenderConfirmLabel } from '../lib/bookingPassengers';

function CloseIcon() {
  return (
    <svg className="booking-confirm-modal__close-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg className="booking-confirm-modal__edit-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );
}

function displayValue(value: string): string {
  const t = value.trim();
  return t || '———';
}

type PassengerSummaryProps = {
  row: BookingPassengerFormRow;
  index: number;
  onEdit: () => void;
};

function PassengerSummary({ row, index, onEdit }: PassengerSummaryProps) {
  return (
    <div className="booking-confirm-modal__passenger">
      <div className="booking-confirm-modal__passenger-head">
        <span className="booking-confirm-modal__passenger-label">Passenger {index + 1}</span>
        <span className="booking-confirm-modal__passenger-gender">
          <span className="booking-confirm-modal__passenger-gender-key">Gender</span>
          <span className="booking-confirm-modal__passenger-gender-val">
            {' '}
            ({formatGenderConfirmLabel(row.gender)})
          </span>
        </span>
        <button type="button" className="booking-confirm-modal__edit" onClick={onEdit}>
          <EditIcon />
          <span>Edit</span>
        </button>
      </div>
      <div className="booking-confirm-modal__fields">
        <div className="booking-confirm-modal__field">
          <div className="booking-confirm-modal__field-label">First name</div>
          <div className="booking-confirm-modal__field-value">{displayValue(row.firstName)}</div>
        </div>
        <div className="booking-confirm-modal__field">
          <div className="booking-confirm-modal__field-label">Last name</div>
          <div className="booking-confirm-modal__field-value">{displayValue(row.lastName)}</div>
        </div>
        <div className="booking-confirm-modal__field">
          <div className="booking-confirm-modal__field-label">Middle name</div>
          <div className="booking-confirm-modal__field-value">{displayValue(row.middleName)}</div>
        </div>
        <div className="booking-confirm-modal__field">
          <div className="booking-confirm-modal__field-label">Date of Birth</div>
          <div className="booking-confirm-modal__field-value">{formatDobConfirmDisplay(row.dateOfBirth)}</div>
        </div>
      </div>
    </div>
  );
}

export type BookingConfirmModalProps = {
  open: boolean;
  passengers: BookingPassengerFormRow[];
  proceeding: boolean;
  onClose: () => void;
  onEditPassenger: (passengerId: string) => void;
  onSaveDetails: () => void;
  onProceed: () => void;
};

export default function BookingConfirmModal({
  open,
  passengers,
  proceeding,
  onClose,
  onEditPassenger,
  onSaveDetails,
  onProceed,
}: BookingConfirmModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setConfirmed(false);
      setShowConfirmError(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !proceeding) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, proceeding]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent) => {
    if (proceeding) return;
    if (e.target === e.currentTarget) onClose();
  };

  const handleSave = () => {
    if (!confirmed) {
      setShowConfirmError(true);
      return;
    }
    setShowConfirmError(false);
    onSaveDetails();
  };

  const handleProceed = () => {
    if (!confirmed) {
      setShowConfirmError(true);
      return;
    }
    setShowConfirmError(false);
    onProceed();
  };

  const content = (
    <div className="booking-confirm-modal-root" role="presentation" onClick={handleBackdrop}>
      <div className="booking-confirm-modal__backdrop" aria-hidden="true" />
      <div className="booking-confirm-modal__container" role="none presentation">
        <div
          className="booking-confirm-modal__paper"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-confirm-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="booking-confirm-modal__header">
            <div id="booking-confirm-modal-title" className="booking-confirm-modal__title">
              Please check and confirm the information for your booking!
            </div>
            <p className="booking-confirm-modal__subtitle">
              The booking will be made with the information provided below. Please note, that after the
              reservation is created, the name changes are not permitted.
            </p>
            <button
              type="button"
              className="booking-confirm-modal__close"
              aria-label="Close"
              disabled={proceeding}
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="booking-confirm-modal__content">
            {passengers.map((row, index) => (
              <PassengerSummary
                key={row.id}
                row={row}
                index={index}
                onEdit={() => onEditPassenger(row.id)}
              />
            ))}
          </div>

          <div className="booking-confirm-modal__actions">
            <div
              className={`booking-confirm-modal__actions-inner${showConfirmError && !confirmed ? ' is-error' : ''}`}
            >
              <label className="booking-confirm-modal__checkbox-label">
                <input
                  type="checkbox"
                  className="booking-confirm-modal__checkbox-input"
                  checked={confirmed}
                  disabled={proceeding}
                  onChange={(e) => {
                    setConfirmed(e.target.checked);
                    if (e.target.checked) setShowConfirmError(false);
                  }}
                />
                <span className="booking-confirm-modal__checkbox-box" aria-hidden />
                <span className="booking-confirm-modal__checkbox-text">
                  I have reviewed the flight itinerary and confirm that the provided names of the passengers
                  match the information in the travel documents.
                </span>
              </label>
              <div className="booking-confirm-modal__buttons">
                <button
                  type="button"
                  className="booking-confirm-modal__save"
                  disabled={proceeding}
                  onClick={handleSave}
                >
                  {proceeding ? 'Saving…' : 'Save details'}
                </button>
                <button
                  type="button"
                  className="booking-confirm-modal__proceed"
                  disabled={proceeding}
                  onClick={handleProceed}
                >
                  {proceeding ? 'Saving…' : 'Proceed to Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
